import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  DEFAULT_PIE_LABEL_POSITION,
  DEFAULT_PIE_LEGEND_FONT_SIZE,
  PIE_LABEL_POSITION_OPTIONS,
  PIE_LEGEND_FONT_SIZE_OPTIONS,
  normalizePieLabelPosition,
  normalizePieLegendFontSize,
  pieLabelsInLegend,
} from '../../utils/pieChartOptions';
import { tooltipDetailHasStyleOptions } from '../../utils/tooltipDetail';

const LINE_INTERPOLATION_OPTIONS = [
  { value: 'linear', label: 'Rectas' },
  { value: 'spline', label: 'Suavizada (spline)' },
  { value: 'step-before', label: 'Escalón — inicio' },
  { value: 'step-middle', label: 'Escalón — medio' },
  { value: 'step-after', label: 'Escalón — fin' },
];

/**
 * Engranaje del subgráfico del tooltip: las mismas opciones de estilo que el gráfico
 * tiene cuando se usa fuera del tooltip.
 */
export default function TooltipDetailStyleButton({ chart, xIsTemporal = false, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const type = chart?.type;
  if (!tooltipDetailHasStyleOptions(type) || typeof onChange !== 'function') return null;

  const style = chart.style || {};
  const patch = (next) => onChange({ ...style, ...next });
  const isPie = type === 'pie' || type === 'doughnut';
  const piePosition = normalizePieLabelPosition(style.pieLabelPosition || DEFAULT_PIE_LABEL_POSITION);

  return (
    <>
      <Tooltip title="Opciones de estilo">
        <IconButton
          size="small"
          aria-label="Opciones de estilo del subgráfico"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            position: 'absolute',
            top: 2,
            left: 2,
            zIndex: 2,
            bgcolor: 'background.paper',
            boxShadow: 1,
            opacity: 0.88,
            '&:hover': { opacity: 1 },
          }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, width: 280, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Estilo
          </Typography>
          {(type === 'bar' || type === 'line' || type === 'scatter') && (
            <>
              <TextField
                size="small"
                label="Título eje X"
                value={style.xAxisTitle || ''}
                onChange={(e) => patch({ xAxisTitle: e.target.value })}
                placeholder="Opcional"
              />
              <TextField
                size="small"
                label="Título eje Y"
                value={style.yAxisTitle || ''}
                onChange={(e) => patch({ yAxisTitle: e.target.value })}
                placeholder="Opcional"
              />
            </>
          )}
          {xIsTemporal && (type === 'bar' || type === 'line') && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={style.fillTimeAxisGaps === true}
                  onChange={(e) => patch({ fillTimeAxisGaps: e.target.checked })}
                />
              }
              label={<Typography variant="body2">Incluir horas/fechas sin datos</Typography>}
            />
          )}
          {type === 'bar' && (
            <>
              {chart.labelColumn ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={style.stacked === true}
                      onChange={(e) => patch({ stacked: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">Barras apiladas</Typography>}
                />
              ) : null}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={style.showValueLabels === true}
                    onChange={(e) => patch({ showValueLabels: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Etiquetas de valores</Typography>}
              />
            </>
          )}
          {type === 'line' && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Interpolación</InputLabel>
                <Select
                  label="Interpolación"
                  value={style.lineInterpolation || 'linear'}
                  onChange={(e) => patch({ lineInterpolation: e.target.value })}
                >
                  {LINE_INTERPOLATION_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={style.lineFillMissingAsZero !== false}
                    onChange={(e) => patch({ lineFillMissingAsZero: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Rellenar huecos con 0</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={style.showValueLabels === true}
                    onChange={(e) => patch({ showValueLabels: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Etiquetas de valores</Typography>}
              />
            </>
          )}
          {isPie && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Ubicación etiquetas</InputLabel>
                <Select
                  label="Ubicación etiquetas"
                  value={piePosition}
                  onChange={(e) => patch({ pieLabelPosition: normalizePieLabelPosition(e.target.value) })}
                >
                  {PIE_LABEL_POSITION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={style.showValueLabel !== false}
                    onChange={(e) => patch({ showValueLabel: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Valor</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={style.showPercentLabel !== false}
                    onChange={(e) => patch({ showPercentLabel: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Porcentaje</Typography>}
              />
              {piePosition === 'outside' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={style.showNameLabel === true}
                      onChange={(e) => patch({ showNameLabel: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">Nombre en callout</Typography>}
                />
              )}
              {pieLabelsInLegend(piePosition) && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Tamaño leyenda</InputLabel>
                  <Select
                    label="Tamaño leyenda"
                    value={normalizePieLegendFontSize(style.pieLegendFontSize || DEFAULT_PIE_LEGEND_FONT_SIZE)}
                    onChange={(e) => patch({ pieLegendFontSize: e.target.value })}
                  >
                    {PIE_LEGEND_FONT_SIZE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" fullWidth>
                <InputLabel>Ubicación leyenda</InputLabel>
                <Select
                  label="Ubicación leyenda"
                  value={style.legendDisplay === false ? 'hidden' : style.legendPosition || 'left'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'hidden') patch({ legendDisplay: false });
                    else patch({ legendDisplay: true, legendPosition: v });
                  }}
                >
                  <MenuItem value="hidden">Oculta</MenuItem>
                  <MenuItem value="left">Izquierda</MenuItem>
                  <MenuItem value="top">Arriba</MenuItem>
                  <MenuItem value="bottom">Abajo</MenuItem>
                  <MenuItem value="right">Derecha</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          {type === 'intervals' && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={style.legendDisplay !== false}
                  onChange={(e) => patch({ legendDisplay: e.target.checked })}
                />
              }
              label={<Typography variant="body2">Mostrar leyenda</Typography>}
            />
          )}
        </Box>
      </Popover>
    </>
  );
}
